import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActivityFilterOptionsQuery } from './api/useActivityFilterOptionsQuery';
import { useActivityQuery } from './api/useActivityQuery';
import {
  defaultFilterSelection,
  isSameAppliedFilterSelection,
  isSameIdSelection,
  normalizeAppliedFilterSelection,
  reconcileAppliedFilterSelection,
  selectedIdsForQuery,
  userIdsForQuery,
  UserActivitySortFilterApply,
  UserActivitySortOrder,
} from './UserActivity.meta';

interface Params {
  clientId: string;
  hideAutomatic: boolean;
  sortFilterEnabled: boolean;
}

export const useUserActivity = ({ clientId, hideAutomatic, sortFilterEnabled }: Params) => {
  const [showFilterPopover, setShowFilterPopover] = useState(false);

  const [sortOrder, setSortOrder] = useState<UserActivitySortOrder>(UserActivitySortOrder.Newest);
  const [userSelectedActivityOptionIds, setUserSelectedActivityOptionIds] = useState<
    string[] | null
  >(null);
  const [userSelectedUserOptionIds, setUserSelectedUserOptionIds] = useState<string[] | null>(null);

  const {
    data: filterOptionsData,
    isLoading: isFilterOptionsLoading,
    isError: isFilterOptionsQueryError,
    refetch: refetchFilterOptions,
  } = useActivityFilterOptionsQuery(clientId, hideAutomatic, sortFilterEnabled);

  const filtersReady =
    !sortFilterEnabled ||
    (!isFilterOptionsLoading && (filterOptionsData !== undefined || isFilterOptionsQueryError));

  const resetFilters = () => {
    setUserSelectedUserOptionIds(null);
    setUserSelectedActivityOptionIds(null);
    setSortOrder(UserActivitySortOrder.Newest);
    setShowFilterPopover(false);
  };

  useEffect(() => {
    resetFilters();
  }, [clientId]);

  const defaultFilters = useMemo(() => defaultFilterSelection(filterOptionsData), [
    filterOptionsData,
  ]);

  const selectedActivityFilterIds = sortFilterEnabled
    ? userSelectedActivityOptionIds ?? defaultFilters.activity
    : [];
  const selectedUserFilterIds = sortFilterEnabled
    ? userSelectedUserOptionIds ?? defaultFilters.user
    : [];

  // Custom selections only: prune stale ids when FilterOptions changes. null always means "select all".
  useEffect(() => {
    if (!filterOptionsData || !sortFilterEnabled) {
      return;
    }

    if (userSelectedUserOptionIds === null && userSelectedActivityOptionIds === null) {
      return;
    }

    const { user, activity } = reconcileAppliedFilterSelection(
      filterOptionsData,
      userSelectedUserOptionIds,
      userSelectedActivityOptionIds,
    );

    if (!isSameAppliedFilterSelection(user, userSelectedUserOptionIds)) {
      setUserSelectedUserOptionIds(user);
    }

    if (!isSameAppliedFilterSelection(activity, userSelectedActivityOptionIds)) {
      setUserSelectedActivityOptionIds(activity);
    }
  }, [
    filterOptionsData,
    sortFilterEnabled,
    userSelectedActivityOptionIds,
    userSelectedUserOptionIds,
  ]);

  const {
    isLoading,
    isError,
    data: activityLog,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    failureCount,
    refetch: refetchActivityLog,
  } = useActivityQuery({
    clientId,
    hideAutomatic,
    sortOrder,
    selectedActivityFilterIds: selectedIdsForQuery(
      selectedActivityFilterIds,
      defaultFilters.activity,
    ),
    selectedUserFilterIds: userIdsForQuery(
      selectedUserFilterIds,
      defaultFilters.user,
      userSelectedUserOptionIds !== null,
    ),
    filtersReady,
  });

  const applySortFilterChange = useCallback(
    ({
      sortOrder: nextSort,
      selectedActivityFilterIds: nextActivityFilterIds,
      selectedUserFilterIds: nextUserFilterIds,
    }: UserActivitySortFilterApply) => {
      const nextActivityForQuery = selectedIdsForQuery(
        nextActivityFilterIds,
        defaultFilters.activity,
      );
      const nextUserForQuery = userIdsForQuery(
        nextUserFilterIds,
        defaultFilters.user,
        !isSameIdSelection(nextUserFilterIds, defaultFilters.user),
      );
      const currentActivityForQuery = selectedIdsForQuery(
        selectedActivityFilterIds,
        defaultFilters.activity,
      );
      const currentUserForQuery = userIdsForQuery(
        selectedUserFilterIds,
        defaultFilters.user,
        userSelectedUserOptionIds !== null,
      );

      if (
        nextSort === sortOrder &&
        isSameIdSelection(nextActivityForQuery, currentActivityForQuery) &&
        isSameIdSelection(nextUserForQuery, currentUserForQuery)
      ) {
        return;
      }

      const normalized = normalizeAppliedFilterSelection(
        nextUserFilterIds,
        nextActivityFilterIds,
        filterOptionsData,
      );

      setSortOrder(nextSort);
      setUserSelectedActivityOptionIds(normalized.activity);
      setUserSelectedUserOptionIds(normalized.user);
    },
    [
      defaultFilters.activity,
      defaultFilters.user,
      filterOptionsData,
      selectedActivityFilterIds,
      selectedUserFilterIds,
      sortOrder,
      userSelectedUserOptionIds,
    ],
  );

  const refetch = useCallback(() => {
    if (sortFilterEnabled && isFilterOptionsQueryError) {
      refetchFilterOptions();
    }

    // ActivityLog auto-fetches when filtersReady flips true after FilterOptions succeeds.
    if (isError && filtersReady) {
      refetchActivityLog();
    }
  }, [
    filtersReady,
    isError,
    isFilterOptionsQueryError,
    refetchActivityLog,
    refetchFilterOptions,
    sortFilterEnabled,
  ]);

  const hasFilterOptions =
    sortFilterEnabled &&
    ((filterOptionsData?.userOptions.length ?? 0) > 0 ||
      Object.keys(filterOptionsData?.activityOptionsByUserId ?? {}).length > 0);

  const isDataFiltered =
    sortFilterEnabled &&
    (userSelectedActivityOptionIds !== null ||
      userSelectedUserOptionIds !== null ||
      sortOrder === UserActivitySortOrder.Oldest);

  return {
    data: {
      sortOrder,
      selectedActivityFilterIds,
      selectedUserFilterIds,
      filterOptions: filterOptionsData ?? null,
      activityLog,
      isLoading: isLoading || (sortFilterEnabled && isFilterOptionsLoading),
      isError,
      isFetching: isFetching && !isFetchingNextPage,
      isFetchingNextPage,
      hasNextPage,
      failureCount,
      hasFilterOptions,
      isDataFiltered,
      showFilterPopover,
    },

    actions: {
      applySortFilterChange,
      refetch,
      fetchNextPage,
      setShowFilterPopover,
      resetFilters,
    },
  };
};
